import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const PreventativeDentistry = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Preventative Dentistry Fort Worth TX | Dental Cleanings & Exams | Fort Worth Dental</title>
        <meta name="description" content="Preventative dentistry in Fort Worth TX. Regular cleanings, exams, fluoride treatments, sealants. Keep your smile healthy. Family dentistry. Call (817) 985-3337." />
        <meta name="keywords" content="preventative dentistry fort worth, dental cleaning fort worth tx, dental exam, teeth cleaning, fluoride treatment, dental sealants" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/services/frame-1288-1.png)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Preventative Dentistry in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Protect Your Smile with Proactive Care</p>
            <p className="text-sm mt-4 opacity-90">Dental Cleanings | Regular Check-Ups | Cavity Prevention</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* What Is Preventative Dentistry */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/preventative-new-1.jpg" 
                  alt="Preventative Dentistry" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288-1.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">What Is Preventative Dentistry?</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Preventative dentistry refers to methods that can help avoid more expensive and painful dental procedures in the future. There is an emphasis on proper oral care at home in order to prevent decay or disease before it even gets started. The proper focus on preventative care can let a patient enjoy good oral health with healthy gums and teeth throughout his life.
                </p>
              </div>
            </div>
          </section>

          {/* What Are Examples */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  What Are Examples of Preventative Dentistry?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  The most common examples of preventative dentistry are daily brushing, daily flossing, and daily rinsing. If these are done at least twice a day, many more serious dental problems will be avoided. However, it is important to carry out these oral hygiene habits properly. Dentists recommend the use of a soft, nylon toothbrush that has round-ended bristles. Holding the toothbrush at a recommended 45-degree angle provides proper coverage of the tooth surface and gum line.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  The patient should move the brush gently back and forth in order to brush away any plaque or tarter off the surface of the teeth and gum line. It is also important to brush the insides of teeth as well as the easier-to-reach outsides of the teeth. Make a special effort to get to hard-to-reach places in the back.
                </p>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/preventative-new-2.jpg" 
                  alt="Daily Brushing Example" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
            </div>
          </section>

          {/* Flossing Instructions */}
          <section className="mb-16 bg-teal-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Proper Flossing Technique</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              The patient can use floss to reach places between the teeth that the toothbrush can't reach. The most effective flossing technique is to take an 18-inch strip of floss and wrap it around the middles finger of each hand. Place the floss between the teeth and use the thumbs to direct the movements of the floss. Use the floss to gently scrape plaque from between the teeth. It is helpful to also floss below the gum line in order to remove plaque that has lodged under the gum line.
            </p>
            <div className="bg-white p-6 rounded-lg">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Flossing Steps:</h3>
              <ol className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 font-bold">1.</span>
                  <span>Take an 18-inch strip of floss and wrap it around the middle finger of each hand</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 font-bold">2.</span>
                  <span>Place the floss between the teeth and use thumbs to direct the movements</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 font-bold">3.</span>
                  <span>Gently scrape plaque from between the teeth</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 font-bold">4.</span>
                  <span>Floss below the gum line to remove plaque that has lodged underneath</span>
                </li>
              </ol>
            </div>
          </section>

          {/* Rinsing */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/preventative-new-1.jpg" 
                  alt="Mouthwash Rinsing" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288-1.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">The Importance of Rinsing</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  Lastly, use a rinse that is specifically designed to kill bacteria and germs. Some mouthwash is nothing more than a breath freshener. Look for antimicrobial rinses that actually kill harmful bacteria in your mouth.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  When combined with regular dental check-ups and professional cleanings, these daily preventative care habits form the foundation of excellent oral health. Your Fort Worth dentist can catch small problems before they become big, expensive issues.
                </p>
              </div>
            </div>
          </section>

          {/* Benefits */}
          <section className="mb-16 bg-gradient-to-br from-purple-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Benefits of Preventative Dentistry</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Prevents cavities and gum disease</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Catches problems early when they're easier to treat</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Saves money on future dental treatments</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Maintains overall health (oral health is linked to heart health)</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Keeps your smile bright and healthy</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Reduces risk of tooth loss</span>
              </li>
            </ul>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-16 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Preventative Care?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              At Fort Worth Dental, we serve patients throughout Fort Worth, Aledo, Benbrook, and surrounding areas. 
              Our comprehensive preventative care program helps you maintain optimal oral health for life.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">30+</div>
                <p className="font-semibold">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">Comprehensive Care</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Schedule Your Preventative Care Appointment</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental for your dental cleaning and exam in Fort Worth, TX</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your Preventative Dentistry Appointment in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PreventativeDentistry;